// =====================================================
// UTILITÁRIO DE FORMATAÇÃO DE MOEDA BRASILEIRA
// =====================================================

/**
 * Formata um valor numérico para o padrão brasileiro (R$ 0.000,00)
 * @param value - Valor numérico ou string
 * @returns String formatada no padrão brasileiro
 */
export function formatCurrency(value: number | string): string {
    if (!value && value !== 0) return 'R$ 0,00'

    const numericValue = typeof value === 'string' ? parseFloat(value) : value

    if (isNaN(numericValue)) return 'R$ 0,00'

    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(numericValue)
}

/**
 * Remove a formatação de moeda e retorna apenas o valor numérico
 * @param formattedValue - Valor formatado (ex: "R$ 1.000,00")
 * @returns Valor numérico
 */
export function parseCurrency(formattedValue: string): number {
    if (!formattedValue) return 0

    // Remove "R$", espaços, pontos e substitui vírgula por ponto
    const cleanValue = formattedValue
        .replace(/R\$\s?/g, '')
        .replace(/\./g, '')
        .replace(',', '.')
        .trim()

    const numericValue = parseFloat(cleanValue)
    return isNaN(numericValue) ? 0 : numericValue
}

/**
 * Aplica máscara de moeda em tempo real durante a digitação
 * @param value - Valor sendo digitado
 * @returns Valor com máscara aplicada
 */
export function applyCurrencyMask(value: string): string {
    if (!value) return ''

    // Remove tudo que não é dígito
    const numbers = value.replace(/\D/g, '')

    if (numbers.length === 0) return ''

    // Converte para número e divide por 100 para ter centavos
    const numericValue = parseInt(numbers) / 100

    // Formata usando Intl.NumberFormat
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(numericValue)
}

/**
 * Aplica máscara de moeda melhorada para digitação
 * @param value - Valor sendo digitado
 * @returns Valor com máscara aplicada
 */
export function applyCurrencyMaskImproved(value: string): string {
    if (!value) return ''

    // Remove tudo que não é dígito
    const numbers = value.replace(/\D/g, '')

    if (numbers.length === 0) return ''

    // Se tem apenas 1 dígito, adiciona zeros à direita
    if (numbers.length === 1) {
        const numericValue = parseInt(numbers) / 100
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(numericValue)
    }

    // Para 2 ou mais dígitos, trata como centavos
    const numericValue = parseInt(numbers) / 100

    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(numericValue)
}

/**
 * Formata valor para exibição em tabelas (sem R$)
 * @param value - Valor numérico
 * @returns String formatada sem símbolo de moeda
 */
export function formatCurrencyDisplay(value: number | string): string {
    if (!value && value !== 0) return '0,00'

    const numericValue = typeof value === 'string' ? parseFloat(value) : value

    if (isNaN(numericValue)) return '0,00'

    return new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(numericValue)
}
